import { getShellInfo } from '../nativeBridge/shellPresence';

import type { OsName } from './types';

export type OsInfo = { os_name?: OsName; os_version?: string };

type UserAgentDataLike = {
  getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>;
};

// Windows NT 10.0 is both 10 and 11, so the user agent only names the releases before it.
const WINDOWS_NT_RELEASES: Record<string, string> = { '6.1': '7', '6.2': '8', '6.3': '8.1' };

const dotted = (raw: string) => raw.replace(/_/g, '.');

/**
 * The OS named by a user agent, and its version only where the string still carries a real one. Chromium
 * reports every Android as 10 and every Mac as 10.15.7, Safari and Firefox freeze macOS the same way, so
 * those versions are left out rather than sent wrong; client hints fill them in on Chromium.
 */
export function parseUserAgentOs(userAgent: string, hasClientHints: boolean, maxTouchPoints = 0): OsInfo {
  const ios = /\b(?:iPhone|iPad|iPod)\b.*? OS (\d+(?:_\d+)*)/.exec(userAgent);
  if (ios) {
    const [major, minor = 0] = ios[1].split('_').map(Number);
    // iOS 26+ freezes the UA's OS at 18.6 / 18.7; Safari's `Version/` still tracks the release.
    if (!(major === 18 && minor >= 6)) return { os_name: 'iOS', os_version: dotted(ios[1]) };
    const safari = /Version\/(\d+(?:\.\d+)*)/.exec(userAgent);
    return safari ? { os_name: 'iOS', os_version: safari[1] } : { os_name: 'iOS' };
  }

  if (/Android/.test(userAgent)) {
    const android = hasClientHints ? null : /Android (\d+(?:\.\d+)*)/.exec(userAgent);
    return android ? { os_name: 'Android', os_version: android[1] } : { os_name: 'Android' };
  }

  const windows = /Windows NT (\d+\.\d+)/.exec(userAgent);
  if (windows) {
    const release = WINDOWS_NT_RELEASES[windows[1]];
    return release ? { os_name: 'Windows', os_version: release } : { os_name: 'Windows' };
  }

  if (/CrOS/.test(userAgent)) return { os_name: 'ChromeOS' };
  // iPadOS asks for the desktop site by default and then reads as a Mac with a touch screen.
  if (/Macintosh/.test(userAgent)) return { os_name: maxTouchPoints > 1 ? 'iOS' : 'macOS' };
  if (/Linux/.test(userAgent)) return { os_name: 'Linux' };
  return {};
}

/** A client hint `platformVersion` as a release: Windows `13.0.0`+ is 11, macOS `15.1.0` is 15.1, Android `14.0.0` is 14. */
export function osVersionFromClientHint(name: OsName | undefined, platformVersion: string | undefined) {
  if (!platformVersion) return undefined;
  if (name === 'Windows') {
    const major = Number.parseInt(platformVersion, 10);
    if (Number.isNaN(major) || major < 1) return undefined;
    return major >= 13 ? '11' : '10';
  }
  if (name === 'macOS' || name === 'Android' || name === 'ChromeOS') {
    return platformVersion.replace(/(\.0)+$/, '') || undefined;
  }
  return undefined;
}

const userAgentData = (): UserAgentDataLike | undefined =>
  (navigator as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData;

let browserOs: OsInfo | null = null;

function readBrowserOs(): OsInfo {
  browserOs ??= parseUserAgentOs(navigator.userAgent, userAgentData() !== undefined, navigator.maxTouchPoints);
  return browserOs;
}

const SHELL_OS_NAMES: Record<string, OsName> = { ios: 'iOS', android: 'Android' };

/** The shell's own report, which outranks the WebView's user agent: iOS WebKit freezes the version there too. */
function readShellOs(): OsInfo | null {
  const info = getShellInfo() as { platform?: unknown; osVersion?: unknown } | null;
  const name = typeof info?.platform === 'string' ? SHELL_OS_NAMES[info.platform] : undefined;
  if (!name) return null;
  const version = typeof info?.osVersion === 'string' && info.osVersion !== '' ? info.osVersion : undefined;
  return version ? { os_name: name, os_version: version } : { os_name: name };
}

/** `os_name` / `os_version` for every event. Keys are left out, never sent empty, when unknown. */
export function getOsInfo(): OsInfo {
  const os = readBrowserOs();
  const shell = readShellOs();
  if (!shell || shell.os_version) return shell ?? os;
  // An older shell sends no version: keep the user agent's when it names the same OS (the freeze rule above vetted it).
  return os.os_name === shell.os_name && os.os_version ? { ...shell, os_version: os.os_version } : shell;
}

/** Fills in the version Chromium withholds from its user agent. Best effort: events before it lands go without. */
export async function resolveOsVersionFromClientHints(): Promise<void> {
  const getHighEntropyValues = userAgentData()?.getHighEntropyValues;
  if (!getHighEntropyValues) return;
  try {
    const { platformVersion } = await getHighEntropyValues.call(userAgentData(), ['platformVersion']);
    const os = readBrowserOs();
    const version = osVersionFromClientHint(os.os_name, platformVersion);
    if (version) browserOs = { ...os, os_version: version };
  } catch {
    // A browser may refuse the hint; the user-agent answer stands.
  }
}

/** Test seam: forget the cached answer. */
export function resetOsInfoForTests(): void {
  browserOs = null;
}
